import { useState, useEffect, useMemo } from 'react';
import { userApi } from '../../api/userApi';
import { toast } from 'react-hot-toast';
import { FiMinus, FiPlus, FiTrash2, FiX } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated } from '../../api/api';

const formatPrice = (price) => {
  if (!price || isNaN(price)) return '₹0';
  return `₹${Number(price).toLocaleString('en-IN')}`;
};

const calculateTotals = (items) => {
  return items.reduce((acc, item) => {
    const basePrice = item.originalPrice;
    const finalPrice = item.finalPrice;
    const quantity = item.quantity;

    const itemSubtotal = basePrice * quantity;
    const itemDiscount = (basePrice - finalPrice) * quantity;
    const itemTotal = finalPrice * quantity;

    return {
      subtotal: acc.subtotal + itemSubtotal,
      totalDiscount: acc.totalDiscount + itemDiscount,
      total: acc.total + itemTotal
    };
  }, { subtotal: 0, totalDiscount: 0, total: 0 });
};

const CartSidebar = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const totals = useMemo(() => calculateTotals(cartItems), [cartItems]);

  const fetchCart = async () => {
    try {
      setIsLoading(true);
      const response = await userApi.getCartItems();
      setCartItems(response || []);
    } catch (error) {
      setError('Failed to load cart items');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && isAuthenticated()) {
      fetchCart();
    }
  }, [isOpen]);

  const handleQuantityChange = async (itemId, newQuantity) => {
    if (newQuantity < 1 || isUpdating) return;
    
    setIsUpdating(true);
    try {
      await userApi.updateCartItemQuantity(itemId, newQuantity);
      
      // Immediately update the cart items with new quantity and recalculated prices
      setCartItems(prevItems =>
        prevItems.map(item => {
          if (item.cartItemId === itemId) {
            return {
              ...item,
              quantity: newQuantity,
            };
          }
          return item;
        })
      );
    } catch (error) {
      toast.error('Failed to update quantity');
      // Refresh cart to ensure consistency
      fetchCart();
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemoveClick = (itemId) => {
    setItemToDelete(itemId);
    setShowDeleteModal(true);
  };

  const confirmRemove = async () => {
    if (!itemToDelete) return;
    
    setIsUpdating(true);
    try {
      await userApi.removeFromCart(itemToDelete);
      setCartItems(prevItems => prevItems.filter(item => item.cartItemId !== itemToDelete));
      toast.success('Item removed from cart');
    } catch (error) {
      toast.error('Failed to remove item');
    } finally {
      setIsUpdating(false);
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  const handleCheckout = () => {
    onClose(); // Close the cart sidebar
    navigate('/checkout'); // Navigate to checkout page
  };

  const CartItem = ({ item, onUpdateQuantity, onRemove }) => {
    const basePrice = item.originalPrice;
    const finalPrice = item.finalPrice;
    const quantity = item.quantity;
    
    
    return (
      <div className="flex py-6 px-4">
        <div className="flex-shrink-0 w-24 h-24">
          <img
            src={item.image || '/images/placeholder-product.png'}
            alt={item.name}
            className="w-full h-full object-cover rounded-md"
          />
        </div>
        <div className="ml-4 flex-1">
          <div className="flex justify-between">
            <div>
              <h3 className="text-base font-medium text-gray-900">{item.name}</h3>
              <p className="mt-1 text-sm text-gray-500">
                {item.color} | {item.size}
              </p>
              
              {/* Quantity Controls */}
              <div className="mt-2 flex items-center border border-gray-200 rounded-lg w-fit overflow-hidden">
                <button
                  onClick={() => onUpdateQuantity(item.cartItemId, quantity - 1)}
                  className={`
                    flex items-center justify-center
                    w-8 h-8
                    transition-all duration-200
                    ${quantity <= 1 
                      ? 'bg-gray-50 cursor-not-allowed' 
                      : 'hover:bg-gray-50 active:bg-gray-100'
                    }
                  `}
                  disabled={quantity <= 1}
                >
                  <FiMinus 
                    className={`
                      w-4 h-4
                      ${quantity <= 1 ? 'text-gray-300' : 'text-gray-600'}
                    `}
                  />
                </button>
                
                <div className="w-10 h-8 flex items-center justify-center border-x border-gray-200">
                  <span className="text-sm font-medium text-gray-900">{quantity}</span>
                </div>
                
                <button
                  onClick={() => onUpdateQuantity(item.cartItemId, quantity + 1)}
                  className={`
                    w-8 h-8 flex items-center justify-center
                    ${quantity >= item.stock
                      ? 'bg-gray-50 cursor-not-allowed' 
                      : 'hover:bg-gray-50 active:bg-gray-100'
                    }
                    transition-all duration-200
                  `}
                  disabled={quantity >= item.stock}
                >
                  <FiPlus 
                    className={`w-4 h-4 ${
                      quantity >= item.stock ? 'text-gray-300' : 'text-gray-600'
                    }`}
                  />
                </button>
              </div>

              {/* Stock Warning */}
              {item.stock <= 5 && (
                <p className="mt-1 text-xs text-red-500">
                  Only {item.stock} left in stock
                </p>
              )}
            </div>
            <div className="flex flex-col items-end">
              <p className="text-base font-medium text-gray-900">
                {formatPrice(finalPrice * quantity)}
              </p>
              {finalPrice < basePrice && (
                <p className="text-sm text-gray-500 line-through">
                  {formatPrice(basePrice * quantity)}
                </p>
              )}
              {/* Remove Button */}
              <button
                onClick={() => onRemove(item.cartItemId)}
                className="mt-2 flex items-center text-sm text-red-600 hover:text-red-800 transition-colors"
              >
                <FiTrash2 className="w-4 h-4 mr-1" />
                Remove
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={`fixed inset-0 bg-black bg-opacity-50 transition-opacity z-50 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
        <div className={`fixed inset-y-0 right-0 max-w-md w-full bg-white shadow-xl transform transition-transform ${isOpen ? 'translate-x-0' : 'translate-x-full'} flex flex-col`}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-6 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Shopping Cart</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>

          {/* Cart Items - Scrollable Area */}
          <div className="flex-1 overflow-y-auto">
            {cartItems.map((item) => (
              <CartItem
                key={item.cartItemId}
                item={item}
                onUpdateQuantity={handleQuantityChange}
                onRemove={handleRemoveClick}
              />
            ))}
          </div>

          {/* Totals and Checkout - Fixed at Bottom */}
          {cartItems.length > 0 && (
            <div className="border-t border-gray-200 bg-white">
              <div className="px-4 py-6 sm:px-6">
                {/* Totals */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-base text-gray-900">
                    <span className="font-medium">Subtotal</span>
                    <span>{formatPrice(totals.subtotal)}</span>
                  </div>
                  
                  {totals.totalDiscount > 0 && (
                    <div className="flex items-center justify-between text-base text-green-600">
                      <span className="font-medium">Discount</span>
                      <span>-{formatPrice(totals.totalDiscount)}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-base font-semibold text-gray-900 border-t border-gray-200 pt-4">
                    <span>Total</span>
                    <span>{formatPrice(totals.total)}</span>
                  </div>
                </div>

                {/* Checkout Button */}
                <button
                  onClick={handleCheckout}
                  className="w-full mt-6 flex justify-center items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-[#2C3E50] hover:bg-[#34495E] transition-colors"
                >
                  Checkout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Remove Item</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to remove this item from your cart?</p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button
                onClick={confirmRemove}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={isUpdating}
              >
                {isUpdating ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CartSidebar;









